import React from 'react';
import { t } from '../../i18n';
import {
  ArrowLeftRight,
  ChevronRight,
  Crown,
  Layers,
  Monitor,
  RefreshCw,
  Undo2,
  UserMinus,
  UserPlus,
} from 'lucide-react';

const ICON = 'w-[18px] h-[18px] text-mute flex-shrink-0';
const CHEVRON = 'w-4 h-4 text-mute flex-shrink-0';

function ToolRow({
  icon: Icon,
  label,
  value,
  subtitle,
  badge,
  trailing,
  chevron = true,
  onClick,
  disabled = false,
  testId,
  ariaLabel,
  role,
  ariaChecked,
  dataOn,
}) {
  const clickable = typeof onClick === 'function' && !disabled;
  const Comp = clickable ? 'button' : 'div';
  const tall = !!subtitle;
  return (
    <Comp
      type={clickable ? 'button' : undefined}
      onClick={clickable ? onClick : undefined}
      role={role}
      aria-checked={ariaChecked}
      data-on={dataOn}
      data-testid={testId}
      aria-label={ariaLabel || label}
      style={clickable ? { WebkitTapHighlightColor: 'transparent' } : undefined}
      className={`host-tools-row w-full flex items-center gap-3 px-3 bg-transparent border-0 text-left ${
        tall ? 'is-tall' : ''
      } ${disabled ? 'opacity-40 pointer-events-none' : ''}`}
    >
      {Icon ? <Icon className={ICON} strokeWidth={1.75} aria-hidden /> : null}
      <span className="flex-1 min-w-0">
        <span className="block text-[14px] font-medium text-ink leading-tight truncate">
          {label}
        </span>
        {subtitle ? (
          <span className="host-tools-sub block text-[11px] text-mute leading-tight mt-0.5">
            {subtitle}
          </span>
        ) : null}
      </span>
      {badge}
      {value ? (
        <span className="text-[13px] text-mute tnum truncate min-w-0 max-w-[46%] text-right">
          {value}
        </span>
      ) : null}
      {trailing}
      {chevron && !disabled ? <ChevronRight className={CHEVRON} aria-hidden /> : null}
    </Comp>
  );
}

/**
 * Grouped Host tools list. Rows keep the existing openers — this is chrome
 * only. Compact 40px rows kick in at max-height 600px. Away blinds lives on
 * Change blinds, not in this GAME group.
 */
export default function HostToolsSheet({
  canUndo,
  undoSnapshot,
  blindsLabel,
  seatedCount = 0,
  bustCount = 0,
  onOpenUndo,
  onOpenChangeBlinds,
  onOpenTableScreen,
  onOpenAddOrRejoin,
  onOpenRebuy,
  onOpenMoveChips,
  onOpenMakeHost,
  onOpenRemovePlayer,
  onEndGame,
}) {
  const undoValue = canUndo
    ? (undoSnapshot?.description || '')
    : t('game.undoNothing');

  return (
    <div className="host-tools-sheet" data-testid="host-tools-list">
      <div className="host-tools-groups">
        <section>
          <div className="label-eyebrow mb-1.5 px-0.5">{t('game.groupGame')}</div>
          <div className="rounded-[14px] border border-line overflow-hidden divide-y divide-line">
            <ToolRow
              icon={Undo2}
              label={t('game.undo')}
              value={undoValue}
              chevron={!!canUndo}
              disabled={!canUndo}
              onClick={canUndo ? onOpenUndo : undefined}
              testId="tool-undo"
              ariaLabel={canUndo ? t('game.undoAction', { action: undoSnapshot?.description || '' }) : t('game.undoNothing')}
            />
            {onOpenChangeBlinds && (
              <ToolRow
                icon={Layers}
                label={t('poker.blinds')}
                value={blindsLabel}
                onClick={onOpenChangeBlinds}
                testId="tool-blinds"
                ariaLabel={t('settings.changeBlinds')}
              />
            )}
            {onOpenTableScreen && (
              <ToolRow
                icon={Monitor}
                label={t('game.tableScreen')}
                onClick={onOpenTableScreen}
                testId="table-screen-pill"
              />
            )}
          </div>
        </section>

        <section>
          <div className="label-eyebrow mb-1.5 px-0.5">{t('game.groupPlayers')}</div>
          <div className="rounded-[14px] border border-line overflow-hidden divide-y divide-line">
            {onOpenAddOrRejoin && (
              <ToolRow
                icon={UserPlus}
                label={t('game.addOrRejoin')}
                value={t('game.seated', { count: seatedCount })}
                onClick={onOpenAddOrRejoin}
                testId="tool-add-or-rejoin"
              />
            )}
            {onOpenRebuy && (
              <ToolRow
                icon={RefreshCw}
                label={t('game.rebuys')}
                onClick={onOpenRebuy}
                testId="tool-rebuys"
                badge={
                  bustCount > 0 ? (
                    <span className="flex-shrink-0 h-5 px-1.5 rounded-full bg-ink text-cream text-[11px] font-medium tnum leading-5">
                      {t('game.bust', { count: bustCount })}
                    </span>
                  ) : null
                }
              />
            )}
            <ToolRow
              icon={ArrowLeftRight}
              label={t('game.moveChips')}
              onClick={onOpenMoveChips}
              testId="tool-move-chips"
            />
            {onOpenMakeHost && (
              <ToolRow
                icon={Crown}
                label={t('game.makeHost')}
                onClick={onOpenMakeHost}
                testId="tool-make-host"
              />
            )}
            {onOpenRemovePlayer && (
              <ToolRow
                icon={UserMinus}
                label={t('game.removePlayer')}
                onClick={onOpenRemovePlayer}
                testId="tool-remove-player"
              />
            )}
          </div>
        </section>
      </div>

      {onEndGame && (
        <button
          type="button"
          onClick={onEndGame}
          style={{ WebkitTapHighlightColor: 'transparent' }}
          className="w-full mt-3 h-11 bg-transparent border-0 text-[15px] font-medium text-accent-danger"
          data-testid="tool-settle-up"
        >
          {t('poker.settleUp')}
        </button>
      )}
    </div>
  );
}
